/**
 * Subscription Tab Component
 * 
 * Displays subscription details, status, and payment history.
 * Allows cancellation of recurring subscriptions.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Modal } from '../ui';
import { 
  getSubscriptionDetails, 
  getPaymentHistory,
  type SubscriptionDetailsResponse,
  type PaymentHistoryItem
} from '../../services/subscription.service';

const SubscriptionTab = () => {
  const { t } = useTranslation();
  
  // State
  const [details, setDetails] = useState<SubscriptionDetailsResponse['data'] | null>(null);
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [detailsResponse, paymentsResponse] = await Promise.all([
        getSubscriptionDetails(),
        getPaymentHistory()
      ]);
      
      if (detailsResponse.success) {
        setDetails(detailsResponse.data);
      }
      
      if (paymentsResponse.success) {
        setPayments(paymentsResponse.data.payments);
        setTotalPaid(paymentsResponse.data.totalPaid);
        setCurrency(paymentsResponse.data.currency);
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError(t('subscriptionTab.error.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!details?.paypalSubscriptionId) return;
    
    setIsCancelling(true);
    try {
      // TODO: Implement cancel subscription API call
      // await cancelSubscription(details.paypalSubscriptionId);
      setShowCancelModal(false);
      await loadData(); // Reload data after cancellation
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
    } finally {
      setIsCancelling(false);
    }
  };
  
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'admin':
        return t('subscriptionTab.plan.admin');
      case 'premium':
        return t('subscriptionTab.plan.premium');
      default:
        return t('subscriptionTab.plan.free');
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
            {t('subscriptionTab.status.active')}
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400">
            {t('subscriptionTab.status.cancelled')}
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
            {t('subscriptionTab.status.expired')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300">
            {t('subscriptionTab.status.free')}
          </span>
        );
    }
  };
  
  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            {t('subscriptionTab.paymentStatus.completed')}
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            {t('subscriptionTab.paymentStatus.pending')}
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            {t('subscriptionTab.paymentStatus.refunded')}
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            {t('subscriptionTab.paymentStatus.failed')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
            {status}
          </span>
        );
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Button onClick={loadData}>{t('common.retry')}</Button>
        </div>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Subscription Status Card */}
      <Card>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('subscriptionTab.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('subscriptionTab.subtitle')}
            </p>
          </div>
          {details && getStatusBadge(details.status)}
        </div>
        
        {details && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Plan */}
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                {t('subscriptionTab.fields.plan')}
              </label>
              <p className="text-slate-900 dark:text-slate-100 font-medium">
                {getPlanLabel(details.plan)}
              </p>
            </div>
            
            {/* Member Since */}
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                {t('subscriptionTab.fields.memberSince')}
              </label>
              <p className="text-slate-900 dark:text-slate-100">
                {formatDate(details.memberSince)}
              </p>
            </div>
            
            {/* Subscription Start Date (only for premium) */}
            {details.plan !== 'free' && details.subscriptionStartDate && (
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('subscriptionTab.fields.startDate')}
                </label>
                <p className="text-slate-900 dark:text-slate-100">
                  {formatDate(details.subscriptionStartDate)}
                </p>
              </div>
            )}
            
            {/* Subscription End Date (only for premium) */}
            {details.plan !== 'free' && details.subscriptionEndDate && (
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('subscriptionTab.fields.endDate')}
                </label>
                <p className="text-slate-900 dark:text-slate-100">
                  {formatDate(details.subscriptionEndDate)}
                  {details.daysRemaining !== null && details.daysRemaining > 0 && (
                    <span className="text-sm text-slate-500 dark:text-slate-400 ml-2">
                      ({details.daysRemaining} {t('subscriptionTab.fields.daysRemaining')})
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {/* Payment Type (only for premium) */}
            {details.plan !== 'free' && details.paymentType && (
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  {t('subscriptionTab.fields.paymentType')}
                </label>
                <p className="text-slate-900 dark:text-slate-100">
                  {details.paymentType === 'subscription_monthly' 
                    ? t('subscriptionTab.paymentTypes.monthly')
                    : (details.paymentType === 'subscription_annual' || details.paymentType === 'subscription')
                    ? t('subscriptionTab.paymentTypes.annual')
                    : t('subscriptionTab.paymentTypes.oneTime')}
                  {details.isRecurring && (
                    <span className="text-sm text-green-600 dark:text-green-400 ml-2">
                      ({t('subscriptionTab.fields.autoRenewal')})
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {/* Admin notice */}
            {details.isAdmin && (
              <div className="col-span-full">
                <div className="p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                  <p className="text-violet-700 dark:text-violet-300 text-sm">
                    {t('subscriptionTab.adminNotice')}
                  </p>
                </div>
              </div>
            )}
            
            {/* Secondary user notice */}
            {details.isSecondaryUser && details.ownerEmail && (
              <div className="col-span-full">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-blue-700 dark:text-blue-300 text-sm">
                    {t('subscriptionTab.secondaryUserNotice', { ownerEmail: details.ownerEmail })}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Cancel Subscription Button */}
        {details?.canCancel && (
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="danger"
              onClick={() => setShowCancelModal(true)}
            >
              {t('subscriptionTab.cancelButton')}
            </Button>
          </div>
        )}
      </Card>
      
      {/* Payment History Card */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('subscriptionTab.history.title')}
          </h3>
          {totalPaid > 0 && (
            <div className="text-right">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {t('subscriptionTab.history.totalPaid')}:
              </span>
              <span className="ml-2 font-semibold text-slate-900 dark:text-slate-100">
                {formatCurrency(totalPaid)}
              </span>
            </div>
          )}
        </div>
        
        {payments.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-4 text-slate-500 dark:text-slate-400">
              {t('subscriptionTab.history.empty')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('subscriptionTab.history.columns.date')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('subscriptionTab.history.columns.type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('subscriptionTab.history.columns.amount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('subscriptionTab.history.columns.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('subscriptionTab.history.columns.reference')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                      {formatDate(payment.createdAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                      {payment.paymentType === 'subscription_monthly'
                        ? t('subscriptionTab.paymentTypes.monthly')
                        : (payment.paymentType === 'subscription_annual' || payment.paymentType === 'subscription')
                        ? t('subscriptionTab.paymentTypes.annual')
                        : t('subscriptionTab.paymentTypes.oneTime')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getPaymentStatusBadge(payment.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 font-mono text-xs">
                      {payment.orderId.substring(0, 12)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      
      {/* Pricing Info Card (for free users who are NOT secondary users) */}
      {details?.plan === 'free' && !details?.isSecondaryUser && (
        <Card>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {t('subscriptionTab.upgrade.title')}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {t('subscriptionTab.upgrade.description')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('subscriptionTab.upgrade.annual')}
              </p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {formatCurrency(details.pricing.annualPrice)}<span className="text-sm font-normal text-slate-500">/an</span>
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                ({formatCurrency(details.pricing.annualMonthlyEquivalent)}/mois)
              </p>
            </div>
            <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('subscriptionTab.upgrade.monthly')}
              </p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {formatCurrency(details.pricing.monthlyPrice)}<span className="text-sm font-normal text-slate-500">/mois</span>
              </p>
            </div>
          </div>
          
          <Button onClick={() => window.location.href = '/premium'}>
            {t('subscriptionTab.upgrade.button')}
          </Button>
        </Card>
      )}
      
      {/* Cancel Subscription Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={t('subscriptionTab.cancel.title')}
      >
        <div className="space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            {t('subscriptionTab.cancel.message')}
          </p>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-amber-700 dark:text-amber-300 text-sm">
              {t('subscriptionTab.cancel.warning')}
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowCancelModal(false)}
              disabled={isCancelling}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleCancelSubscription}
              disabled={isCancelling}
            >
              {isCancelling ? t('common.loading') : t('subscriptionTab.cancel.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionTab;
